/* eslint-disable filename-rules/match */
import { Language } from "@src/reducers/your-reducer";
import {
  englishModules,
  germanModules,
  italianModules,
  simplifiedChineseModules,
  spanishModules,
  brazilianPortugueseModules,
  dutchModules,
  turkishModules,
} from "./translations/modules";
import {
  englishPages,
  germanPages,
  italianPages,
  simplifiedChinesePages,
  spanishPages,
  brazilianPortuguesePages,
  dutchPages,
  turkishPages,
} from "./translations/pages";
import * as Sentry from "@sentry/browser";

const translations = {
  en: {
    pages: {
      ...englishPages,
    },
    modules: {
      ...englishModules,
    },
  },
  de: {
    pages: {
      ...germanPages,
    },
    modules: {
      ...germanModules,
    },
  },
  zh: {
    pages: {
      ...simplifiedChinesePages,
    },
    modules: {
      ...simplifiedChineseModules,
    },
  },
  it: {
    pages: {
      ...italianPages,
    },
    modules: {
      ...italianModules,
    },
  },
  es: {
    pages: {
      ...spanishPages,
    },
    modules: {
      ...spanishModules,
    },
  },
  pt: {
    pages: {
      ...brazilianPortuguesePages,
    },
    modules: {
      ...brazilianPortugueseModules,
    },
  },
  nl: {
    pages: {
      ...dutchPages,
    },
    modules: {
      ...dutchModules,
    },
  },
  tr: {
    pages: { ...turkishPages },
    modules: { ...turkishModules },
  },
};

const DEFAULT_LANG = "en";

/**
 * Translates a key to the corresponding string in the specified language.
 * If the translation is not found, an error message is returned.
 *
 * @param {string} key - The key to be translated.
 * @param {string} [language=DEFAULT_LANG] - The language for which the translation is requested.
 * @returns {string} The translated string or an error message if the translation is missing.
 *
 * @example
 * // Using the i18n function
 */
export const i18n = (
  key: string,
  language: Language = DEFAULT_LANG,
): string => {
  if (!language) {
    language = "en";
  }

  const keys = key.split(".");
  let currentTranslation = translations[language];

  for (const k of keys) {
    currentTranslation = currentTranslation && currentTranslation[k];
  }

  if (typeof currentTranslation === "string") {
    return currentTranslation;
  } else {
    // This check is to avoid having hundreds of Sentry errors in development mode (localhost)
    if (process.env.NODE_ENV !== "development") {
      Sentry.withScope((scope) => {
        scope.setTag("type", "i18n_missing");
        scope.setExtra("key", key);
        scope.setExtra("language", language);

        Sentry.captureException(
          new Error(`error: missing translation for ${key} in ${language}`),
        );
      });
    }

    return `error: missing translation for ${key}`;
  }
};
